<template>
  <div class="ant-qty-selector" :class="[`ant-qty-selector--${size}`]">
    <h-button
      type="text"
      class="ant-qty-selector__btn"
      :disabled="disabled || innerValue <= min"
      @click="decrease"
    >
      <i class="fas fa-minus" />
    </h-button>

    <a-input-number
      v-model:value="innerValue"
      :min="min"
      :max="max"
      :step="step"
      :disabled="disabled"
      :controls="false"
      :precision="0"
      class="ant-qty-selector__input"
      @blur="onBlur"
    />

    <h-button
      type="text"
      class="ant-qty-selector__btn"
      :disabled="disabled || innerValue >= max"
      @click="increase"
    >
      <i class="fas fa-plus" />
    </h-button>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  size?: SizeType;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 1,
  min: 1,
  max: 999,
  step: 1,
  disabled: false,
  size: "middle",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: number): void;
  (e: "change", value: number): void;
  (e: "blur", event: FocusEvent): void;
}>();

const clamp = (val: number) => {
  let next = Number.isFinite(val) ? val : props.min;
  if (next < props.min) next = props.min;
  if (next > props.max) next = props.max;
  return next;
};

const innerValue = computed({
  get: () => clamp(props.modelValue ?? props.min),
  set: (val: number | null) => {
    const next = clamp(val ?? props.min);
    emit("update:modelValue", next);
    emit("change", next);
  },
});

const decrease = () => {
  innerValue.value = clamp(innerValue.value - props.step);
};

const increase = () => {
  innerValue.value = clamp(innerValue.value + props.step);
};

const onBlur = (e: FocusEvent) => emit("blur", e);
</script>

<style scoped>
.ant-qty-selector {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.ant-qty-selector__btn {
  width: 36px;
  height: 36px;
  padding: 0;
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  color: #555;
}

.ant-qty-selector__btn:hover:not(:disabled) {
  color: #b19663;
  background: #faf7f2 !important;
}

.ant-qty-selector__input {
  width: 56px !important;
  border: none !important;
  border-left: 1px solid #eee !important;
  border-right: 1px solid #eee !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.ant-qty-selector__input :deep(.ant-input-number-input) {
  text-align: center;
  height: 34px;
  padding: 0 4px;
}

.ant-qty-selector--small .ant-qty-selector__btn,
.ant-qty-selector--small .ant-qty-selector__input :deep(.ant-input-number-input) {
  height: 28px;
}

.ant-qty-selector--small .ant-qty-selector__btn {
  width: 28px;
}

.ant-qty-selector--small .ant-qty-selector__input {
  width: 44px !important;
}

.ant-qty-selector--large .ant-qty-selector__btn {
  width: 44px;
  height: 44px;
}

.ant-qty-selector--large .ant-qty-selector__input {
  width: 72px !important;
}

.ant-qty-selector--large .ant-qty-selector__input :deep(.ant-input-number-input) {
  height: 42px;
}
</style>

<!--
  Hướng dẫn sử dụng — Quantity Selector
  Global: <h-quantity v-model="qty" :min="1" :max="99" />
  Import: import { QuantitySelector } from '@/components/ant/product'

  Props: modelValue (number), min, max, step, disabled, size
  Events: update:modelValue, change, blur
  Docs: src/components/ant/product/README.md
-->
